import classes from "./stage3.module.css";
import Button from "../../../components/UI/Button/button";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { errorHandler } from "../../../utils";
import { companyInfo, companyProfile } from "../../../services/clientCalls";
import { CalenderIcon } from "../../../constants/svg";
import {
  checkMail,
  convertToBase64,
  isValidURL,
  numericOnly,
} from "../../../utils/functions";
import PanelHero from "../PanelHero/PanelHero";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBuildingCommunity,
  IconChevronsLeft,
  IconFileUpload,
  IconShieldCheck,
  IconWorld,
} from "@tabler/icons-react";
import Input, {
  InfoTooltip,
  Select,
} from "../../../components/UI/FormInput/input";
import s from "../steps.module.css";
import statesData from "../../../utils/states.json";
import moment from "moment";
import { LOCATIONS } from "../../../components/Landing/components/Checkout/countries";

const POSITIONS = [
  "CEO / Founder",
  "Managing Director",
  "Project Manager",
  "HR Manager",
  "Operations Manager",
  "Recruitment Manager",
  "Admin Officer",
  "Other",
];

const Stage3 = () => {
  let navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date());
  const [load, setLoad] = useState(false);
  const [loader, setLoader] = useState(false);
  const [infoData, setInfoData] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    companyName: "",
    position: "",
    email: "",
    yearOfEstablishment: startDate,
    companyAddress: "",
    companyWebsiteUrl: "",
    taxIdentificationNumber: "",
    cacNumber: "",
    location: "",
    companyRegisterationNumber: "",
    taxOrVatNumber: "",
    certificateOfIncorporation: "",
  });
  const [countries, setCountries] = useState([]);
  const [incorpDoc, setIncorpDoc] = useState(null);
  const isNigeria = data?.location?.toLowerCase() === "nigeria";

  const getCompanyProfile = async () => {
    setLoader(true);
    try {
      const res = await companyProfile();
      setInfoData(res.data.data.client);
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };

  useEffect(() => {
    getCompanyProfile();
  }, []);

  useEffect(() => {
    if (infoData) {
      setData((prevState) => ({
        ...prevState,
        companyName: infoData?.companyName ?? "",
        position: infoData?.position ?? "",
        email: infoData?.email ?? "",
        companyAddress: infoData?.companyAddress ?? "",
        companyWebsiteUrl: infoData?.companyWebsiteUrl ?? "",
        taxIdentificationNumber: infoData?.taxIdentificationNumber ?? "",
        cacNumber: infoData?.cacNumber ?? "",
        location: infoData?.location ?? "",
        companyRegisterationNumber: infoData?.companyRegisterationNumber ?? "",
        taxOrVatNumber: infoData?.taxOrVatNumber ?? "",
        certificateOfIncorporation: infoData?.certificateOfIncorporation ?? "",
      }));
      setStartDate(
        infoData?.yearOfEstablishment
          ? moment(infoData?.yearOfEstablishment).format("YYYY-MM-DD")
          : "",
      );
      window.scrollTo(0, 0);
    }
  }, [infoData]);

  const submit = async (e) => {
    e.preventDefault();

    if (validate())
      if (incorpDoc) {
        const base64Image = await convertToBase64(incorpDoc);
        data.certificateOfIncorporation = base64Image;
      }
    try {
      setLoad(true);
      await companyInfo(data);
      navigate({
        pathname: "/client-onboarding",
        search: "stage=4",
      });
      setLoad(false);
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const validate = () => {
    const errs = {};
    if (!data.companyName.trim())
      errs.companyName = "Company name is required.";
    if (!data.position) errs.position = "Please select your position.";
    if (!data.email.trim() || !checkMail(data.email))
      errs.email = "Enter a valid email address.";
    if (!data.yearOfEstablishment)
      errs.yearOfEstablishment = "Please select a date.";
    if (!data.companyAddress.trim())
      errs.companyAddress = "Address is required.";
    if (!data.cacNumber.trim()) errs.cacNumber = "CAC number is required.";
    if (!data.taxIdentificationNumber.trim())
      errs.taxIdentificationNumber = "Tax ID is required.";
    if (data.companyWebsiteUrl.trim() && !isValidURL(data.companyWebsiteUrl))
      errs.companyWebsiteUrl = "Enter a valid website (e.g. company.com).";
    setErrors(errs);

    // Scroll to first error
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(firstKey);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus({ preventScroll: true });
      }
    }

    return Object.keys(errs).length === 0;
  };

  useEffect(() => {
    setData({
      ...data,
      yearOfEstablishment: startDate,
    });
  }, [startDate]);

  useEffect(() => {
    const allLocations = [];

    Object.values(statesData)?.forEach((country) => {
      allLocations.push({
        label: country.name,
        value: country.name,
        type: "country",
      });
    });

    const priority = ["Nigeria", "United Kingdom"];

    allLocations?.sort((a, b) => {
      const aIndex = priority.indexOf(a.label);
      const bIndex = priority.indexOf(b.label);

      // Both are priority countries
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // Only one is a priority country
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Sort the rest alphabetically
      return a.label.localeCompare(b.label);
    });

    setCountries(allLocations);
  }, []);

  return (
    <div className={classes.container}>
      <PanelHero
        icon={<IconBuildingCommunity />}
        title="Company Information"
        subtitle="Please provide us with basic information about your company"
      />

      <form>
        <div className={s.panelBody}>
          {/* Company Name */}
          <div className={s.formRow} style={{ gridTemplateColumns: "1fr" }}>
            <Input
              type="text"
              label="Company Name"
              required
              htmlFor="companyName"
              id="companyName"
              placeholder="Awasource"
              value={data.companyName}
              error={errors.companyName}
              onChange={(e) => {
                setData({ ...data, companyName: e.target.value });
                setErrors((er) => ({ ...er, companyName: "" }));
              }}
            />
          </div>

          {/* Position + Email */}
          <div className={s.formRow}>
            <Select
              label="Position"
              required
              htmlFor="position"
              id="position"
              value={data.position}
              onChange={(e) => {
                setData((prev) => ({ ...prev, position: e.target.value }));
                setErrors((prev) => ({ ...prev, position: "" }));
              }}
              error={errors.position}
              shake={errors.position}
            >
              <option value={data.position}>
                {data.position || "Select position…"}
              </option>
              {POSITIONS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </Select>

            <Input
              type="email"
              label="Email Address"
              required
              htmlFor="email"
              id="email"
              placeholder="jane@yourcompany.com"
              value={data.email}
              error={errors.email}
              shake={errors.email}
              onChange={(e) => {
                setData((prev) => ({ ...prev, email: e.target.value }));
                setErrors((er) => ({ ...er, email: "" }));
              }}
            />
          </div>

          {/* Year Founded + Website */}
          <div className={s.formRow}>
            <Input
              type="date"
              label="Year Of Establishment"
              required
              rightIcon={<CalenderIcon />}
              htmlFor="yearOfEstablishment"
              id="yearOfEstablishment"
              placeholder=""
              tooltip="The official date your company was registered or incorporated."
              value={data.yearOfEstablishment}
              error={errors.yearOfEstablishment}
              shake={errors.yearOfEstablishment}
              onChange={(e) => {
                setData({ ...data, yearOfEstablishment: e.target.value });
                setErrors((er) => ({ ...er, yearOfEstablishment: "" }));
              }}
            />

            <Input
              type="text"
              label="Company Website URL"
              leftIcon={<IconWorld size={16} />}
              htmlFor="companyWebsiteUrl"
              id="companyWebsiteUrl"
              placeholder="yourcompany.com"
              tooltip="The official website URL of your company."
              value={data.companyWebsiteUrl}
              error={errors.companyWebsiteUrl}
              shake={errors.companyWebsiteUrl}
              onChange={(e) => {
                setData({ ...data, companyWebsiteUrl: e.target.value });
                setErrors((er) => ({ ...er, companyWebsiteUrl: "" }));
              }}
            />
          </div>

          {/* Address + CAC Number */}
          <div className={s.formRow}>
            <Select
              label="Location"
              required
              htmlFor="location"
              id="location"
              value={data.location}
              onChange={(e) => {
                setData((prev) => ({ ...prev, location: e.target.value }));
                setErrors((prev) => ({ ...prev, location: "" }));
              }}
              error={errors.location}
              shake={errors.location}
            >
              <option value="">Select location…</option>
              {LOCATIONS?.map((p) => (
                <option key={p.value}>{p.label}</option>
              ))}
            </Select>
            <Input
              type="text"
              label="Registered Company Address"
              required
              htmlFor="companyAddress"
              id="companyAddress"
              placeholder="Registered company address"
              tooltip="Your company's official registered address as it appears on your incorporation documents."
              value={data.companyAddress}
              error={errors.companyAddress}
              shake={errors.companyAddress}
              onChange={(e) => {
                setData({ ...data, companyAddress: e.target.value });
                setErrors((er) => ({ ...er, companyAddress: "" }));
              }}
            />
          </div>

          {isNigeria && data?.location && (
            <div className={s.formRow} style={{ marginBottom: 18 }}>
              <Input
                type="text"
                label="CAC Number"
                required
                htmlFor="cacNumber"
                id="cacNumber"
                placeholder="CAC Number"
                tooltip="Your Corporate Affairs Commission registration number assigned during company incorporation."
                value={data.cacNumber}
                error={errors.cacNumber}
                shake={errors.cacNumber}
                onChange={(e) => {
                  setData({ ...data, cacNumber: e.target.value });
                  setErrors((er) => ({ ...er, cacNumber: "" }));
                }}
              />
              <Input
                type="text"
                label="Tax Identification Number"
                required
                htmlFor="taxIdentificationNumber"
                id="taxIdentificationNumber"
                placeholder="Tax Identification Number"
                tooltip="Issued by the Federal Inland Revenue Service (FIRS) to identify your company for tax purposes."
                value={data.taxIdentificationNumber}
                error={errors.taxIdentificationNumber}
                shake={errors.taxIdentificationNumber}
                onInput={numericOnly}
                onChange={(e) => {
                  setData({ ...data, taxIdentificationNumber: e.target.value });
                  setErrors((er) => ({ ...er, taxIdentificationNumber: "" }));
                }}
              />
            </div>
          )}

          {!isNigeria && data?.location && (
            <div className={s.kycBlock}>
              <div className={s.infoBanner}>
                <IconShieldCheck size={15} />
                <span>
                  Because your company is registered outside Nigeria, we verify
                  these international equivalents.
                </span>
              </div>

              <div className={s.formRow}>
                <Input
                  type="text"
                  label="Company Registration Number"
                  required
                  htmlFor="companyRegisterationNumber"
                  id="companyRegisterationNumber"
                  placeholder="Registration / incorporation number"
                  tooltip="Your certificate-of-incorporation number from the local company registry — e.g. Companies House (UK), state Certificate of Incorporation / EIN (US), or your national trade register."
                  value={data.companyRegisterationNumber}
                  error={errors.companyRegisterationNumber}
                  shake={errors.companyRegisterationNumber}
                  onInput={numericOnly}
                  onChange={(e) => {
                    setData({
                      ...data,
                      companyRegisterationNumber: e.target.value,
                    });
                    setErrors((er) => ({
                      ...er,
                      companyRegisterationNumber: "",
                    }));
                  }}
                />
                <Input
                  type="text"
                  label="Tax ID / VAT Number"
                  required
                  htmlFor="taxOrVatNumber"
                  id="taxOrVatNumber"
                  placeholder="Tax ID or VAT number"
                  tooltip="Your company's local tax or VAT number — e.g. EIN (US), VAT number (EU/UK), or the equivalent business tax ID in your country."
                  value={data.taxOrVatNumber}
                  error={errors.taxOrVatNumber}
                  shake={errors.taxOrVatNumber}
                  onInput={numericOnly}
                  onChange={(e) => {
                    setData({
                      ...data,
                      taxOrVatNumber: e.target.value,
                    });
                    setErrors((er) => ({ ...er, taxOrVatNumber: "" }));
                  }}
                />
              </div>

              <div className={`${s.formRow}`} style={{ marginBottom: 18 }}>
                <div className="flex flex-col gap-1.5">
                  <label className={s.formLabelInfo} htmlFor="incorpDoc">
                    <span>
                      Certificate of Incorporation{" "}
                      <span className={s.optionalNote}>(optional)</span>
                    </span>
                    <InfoTooltip text="Upload your certificate of incorporation or business-registration document. PDF, JPG or PNG, max 5 MB." />
                  </label>
                  <label
                    htmlFor="incorpDoc"
                    className={`${s.docUpload} ${
                      incorpDoc || data?.certificateOfIncorporation
                        ? s.hasFile
                        : ""
                    }`}
                  >
                    <IconFileUpload size={17} />
                    <span>
                      {incorpDoc
                        ? incorpDoc.name
                        : data?.certificateOfIncorporation
                          ? "File already uploaded"
                          : "Upload PDF"}
                    </span>
                  </label>
                  <input
                    id="incorpDoc"
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={(e) => {
                      setIncorpDoc(e.target.files[0]);
                    }}
                  />
                  {errors.incorpDoc && (
                    <div className={s.fieldErrOn}>
                      <IconAlertCircle size={13} /> {errors.incorpDoc}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={s.cautionNote}>
            <IconAlertTriangle size={20} color="#d97706" />
            <span>
              <strong>Note:</strong> Verification is restricted to clients who
              provide accurate and verifiable information in this section. Only
              verified accounts gain access to match with talents and create
              jobs.
            </span>
          </div>
        </div>

        <div className={s.panelFooter}>
          <div className={s.footerNote}>
            <Button
              variant="ghost"
              leftIcon={<IconChevronsLeft size={15} />}
              onClick={() => navigate(-1)}
              disabled={load}
            >
              Back
            </Button>
          </div>
          <div className={s.footerActions}>
            <Button onClick={submit} disabled={load} loading={load}>
              Next
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Stage3;
